import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Loader2, User, Calendar, Clock, Heart } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LocationInput } from './LocationInput'
import { BirthTimezoneField, type BirthTimezoneState } from './BirthTimezoneField'
import { isRealCalendarDate, toIsoBirthDate } from '@/utils/birthDate'
import { auth } from '@/firebaseConfig'
import { getSecureHeaders } from '@/services'
import { useTranslation } from 'react-i18next'

interface MarriageCompatibilityFormProps {
  onCompatibilityGenerated: (data: unknown) => void
}

interface PersonFormData {
  name: string
  birthDate: string
  birthTime: string
  latitude: string
  longitude: string
  timezoneOffset: string
  timezoneName?: string
  timezoneSource: '' | 'auto' | 'manual'
  timezoneFold: number | null
  timezoneResolverVersion: number | null
  locationName: string
  country?: string
  countryName?: string
}

const MarriageCompatibilityForm = ({ onCompatibilityGenerated }: MarriageCompatibilityFormProps) => {
  const [loading, setLoading] = useState(false)
  const { toast } = useToast()
  const { t } = useTranslation('chat')

  // Boy's State
  // No seeded offset — see BirthChartForm. Resolved per partner from their own
  // birthplace and birth instant; a couple can be born in two different zones.
  const [boyFormData, setBoyFormData] = useState<PersonFormData>({
    name: '', birthDate: '', birthTime: '', latitude: '', longitude: '', timezoneOffset: '', timezoneName: '', timezoneSource: '', timezoneFold: null, timezoneResolverVersion: null, locationName: '', country: '', countryName: ''
  })
  const [boyTzState, setBoyTzState] = useState<BirthTimezoneState>({
    offsetHours: null, timezoneName: null, status: 'idle', usable: false, source: null, fold: null, resolverVersion: null,
  })
  const [boyBirthTimeDisplay, setBoyBirthTimeDisplay] = useState('')
  const [boyBirthTimeInput, setBoyBirthTimeInput] = useState('')
  const [boyBirthDateInput, setBoyBirthDateInput] = useState('')
  const [boyBirthEra, setBoyBirthEra] = useState<'CE' | 'BCE'>('CE')
  const [boyBirthDateError, setBoyBirthDateError] = useState('')
  const [boyLoaded, setBoyLoaded] = useState(false)

  // Girl's State
  const [girlFormData, setGirlFormData] = useState<PersonFormData>({
    name: '', birthDate: '', birthTime: '', latitude: '', longitude: '', timezoneOffset: '', timezoneName: '', timezoneSource: '', timezoneFold: null, timezoneResolverVersion: null, locationName: '', country: '', countryName: ''
  })
  const [girlTzState, setGirlTzState] = useState<BirthTimezoneState>({
    offsetHours: null, timezoneName: null, status: 'idle', usable: false, source: null, fold: null, resolverVersion: null,
  })
  const [girlBirthTimeDisplay, setGirlBirthTimeDisplay] = useState('')
  const [girlBirthTimeInput, setGirlBirthTimeInput] = useState('')
  const [girlBirthDateInput, setGirlBirthDateInput] = useState('')
  const [girlBirthEra, setGirlBirthEra] = useState<'CE' | 'BCE'>('CE')
  const [girlBirthDateError, setGirlBirthDateError] = useState('')
  const [girlLoaded, setGirlLoaded] = useState(false)

  // Persistence for Boy
  useEffect(() => {
    const savedBoy = localStorage.getItem('boyFormData')
    if (savedBoy) {
      try {
        const parsed = JSON.parse(savedBoy)
        // Pre-fix saves carry a seeded `5.5` and no IANA zone; drop and re-resolve.
        const savedForm = { ...(parsed.formData || {}) }
        if (parsed.schemaVersion !== 3 || savedForm.timezoneSource !== 'manual') {
          delete savedForm.timezoneOffset
          delete savedForm.timezoneSource
          delete savedForm.timezoneFold
          delete savedForm.timezoneResolverVersion
        }
        setBoyFormData((prev) => ({ ...prev, ...savedForm }))
        setBoyBirthTimeInput(parsed.timeInput || '')
        setBoyBirthDateInput(parsed.dateInput || '')
        setBoyBirthTimeDisplay(parsed.timeDisplay || '')
        setBoyBirthEra(parsed.era || 'CE')
      } catch {
        void 0
      }
    }
    setBoyLoaded(true)
  }, [])

  useEffect(() => {
    if (!boyLoaded) return
    const timeoutId = window.setTimeout(() => {
      try {
        localStorage.setItem('boyFormData', JSON.stringify({
          schemaVersion: 3,
          formData: boyFormData,
          timeInput: boyBirthTimeInput,
          dateInput: boyBirthDateInput,
          timeDisplay: boyBirthTimeDisplay,
          era: boyBirthEra
        }))
      } catch {
        void 0
      }
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [boyFormData, boyBirthTimeInput, boyBirthDateInput, boyBirthTimeDisplay, boyBirthEra, boyLoaded])

  // Persistence for Girl
  useEffect(() => {
    const savedGirl = localStorage.getItem('girlFormData')
    if (savedGirl) {
      try {
        const parsed = JSON.parse(savedGirl)
        const savedForm = { ...(parsed.formData || {}) }
        if (parsed.schemaVersion !== 3 || savedForm.timezoneSource !== 'manual') {
          delete savedForm.timezoneOffset
          delete savedForm.timezoneSource
          delete savedForm.timezoneFold
          delete savedForm.timezoneResolverVersion
        }
        setGirlFormData((prev) => ({ ...prev, ...savedForm }))
        setGirlBirthTimeInput(parsed.timeInput || '')
        setGirlBirthDateInput(parsed.dateInput || '')
        setGirlBirthTimeDisplay(parsed.timeDisplay || '')
        setGirlBirthEra(parsed.era || 'CE')
      } catch {
        void 0
      }
    }
    setGirlLoaded(true)
  }, [])

  useEffect(() => {
    if (!girlLoaded) return
    const timeoutId = window.setTimeout(() => {
      try {
        localStorage.setItem('girlFormData', JSON.stringify({
          schemaVersion: 3,
          formData: girlFormData,
          timeInput: girlBirthTimeInput,
          dateInput: girlBirthDateInput,
          timeDisplay: girlBirthTimeDisplay,
          era: girlBirthEra
        }))
      } catch {
        void 0
      }
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [girlFormData, girlBirthTimeInput, girlBirthDateInput, girlBirthTimeDisplay, girlBirthEra, girlLoaded])

  // Helper Functions (Duplicated from BirthChartForm for independence)
  const formatDMY = (raw: string) => {
    const digits = (raw || '').replace(/[^0-9]/g, '')
    const dd = digits.slice(0, 2)
    const mm = digits.slice(2, 4)
    const y = digits.slice(4, 9)
    let out = ''
    if (dd) out += dd
    if (mm) out += `-${mm}`
    if (y) out += `-${y}`
    return { dd, mm, y, display: out }
  }

  const validateDateParts = (dd: string, mm: string, y: string, era: 'CE' | 'BCE') => {
    const day = parseInt(dd || '0', 10)
    const mon = parseInt(mm || '0', 10)
    const yearDigits = y || ''
    if (yearDigits.length > 5) return t('chat.astro_form.errors.year_max_digits')
    if (mon && (mon < 1 || mon > 12)) return t('chat.astro_form.errors.invalid_month')
    if (day && (day < 1 || day > 31)) return t('chat.astro_form.errors.invalid_day')
    if (yearDigits) {
      const yr = parseInt(yearDigits, 10)
      if (era === 'BCE' && yr > 13200) return t('chat.astro_form.errors.bce_year_max')
      if (era === 'CE' && yr > 17191) return t('chat.astro_form.errors.ce_year_max')
      // Only once all three parts are present: does this date actually EXIST?
      // The range checks above accept 31 February and 29 February 2025. Swiss
      // Ephemeris then silently computes the rolled-over day — verified, 1986-02-31
      // produced a chart identical to 1986-03-03 — so a plausible horoscope came
      // back for a date the user never entered. The backend rejects these too.
      if (dd && mm && !isRealCalendarDate(era === 'BCE' ? -yr : yr, mon, day)) {
        return t('chat.astro_form.errors.invalid_day')
      }
    }
    return ''
  }

  const parseHumanTimeInput = (input: string) => {
    const raw = input.trim().toLowerCase()
    if (!raw) return { hhmm: '', display: '', complete: false, normalizedInput: '', suffixIntent: '' }

    const intentMatch = raw.match(/(am?|pm?)$/)
    const suffixIntent = intentMatch ? (intentMatch[0].startsWith('p') ? 'PM' : 'AM') : ''
    const isPM = /pm$/.test(raw)
    const isAM = /am$/.test(raw)
    const cleaned = raw.replace(/\s*(am|pm)\s*$/,'').replace(/\s+/g,'')

    let hours = 0
    let minutes = 0
    let normalizedInput = ''

    if (/^\d{1,2}[.:]\d{1,2}$/.test(cleaned)) {
      const [h, m] = cleaned.split(/[.:]/)
      hours = parseInt(h, 10)
      minutes = parseInt(m, 10)
      if (!isAM && !isPM && m.length === 2) {
        normalizedInput = `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`
      }
    } else if (/^\d{3,4}$/.test(cleaned)) {
      const len = cleaned.length
      const h = cleaned.slice(0, len - 2)
      const m = cleaned.slice(len - 2)
      hours = parseInt(h, 10)
      minutes = parseInt(m, 10)
      if (!isAM && !isPM && len === 4) {
        normalizedInput = `${String(hours).padStart(2,'0')}:${String(minutes).padStart(2,'0')}`
      }
    } else if (/^\d{1,2}$/.test(cleaned)) {
      hours = parseInt(cleaned, 10)
      minutes = 0
    } else {
      return { hhmm: '', display: '', complete: false, normalizedInput: '', suffixIntent: '' }
    }

    hours = Math.max(0, Math.min(23, hours))
    minutes = Math.max(0, Math.min(59, minutes))

    if (isPM && hours < 12) hours += 12
    if (isAM && hours === 12) hours = 0

    const hh = String(hours).padStart(2,'0')
    const mm = String(minutes).padStart(2,'0')
    const hours12 = ((hours + 11) % 12) + 1
    const suffix = hours >= 12 ? 'PM' : 'AM'
    const display = `${hours12}:${mm} ${suffix}`
    const hasAmPm = isAM || isPM
    const complete = hasAmPm
      ? /^\d{1,2}([:.]\d{1,2})?$/.test(cleaned)
      : (/^\d{1,2}([:.]\d{2})$/.test(cleaned) || /^\d{4}$/.test(cleaned))
    return { hhmm: `${hh}:${mm}`, display, complete, normalizedInput, suffixIntent }
  }

  // Handlers Generator
  const createHandlers = (
    setFormData: React.Dispatch<React.SetStateAction<PersonFormData>>,
    setBirthDateInput: React.Dispatch<React.SetStateAction<string>>,
    setBirthEra: React.Dispatch<React.SetStateAction<'CE' | 'BCE'>>,
    setBirthDateError: React.Dispatch<React.SetStateAction<string>>,
    birthEra: 'CE' | 'BCE',
    birthDateInput: string,
    setBirthTimeDisplay: React.Dispatch<React.SetStateAction<string>>,
    setBirthTimeInput: React.Dispatch<React.SetStateAction<string>>,
    birthTimeInput: string,
    birthTimeDisplay: string
  ) => {
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleBirthDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const eraHint = /--/.test(raw) ? 'BCE' : 'CE'
      const { dd, mm, y, display } = formatDMY(raw)
      const displayWithEra = eraHint === 'BCE' && y ? `${dd}${mm ? `-${mm}` : ''}--${y}` : display
      setBirthEra(eraHint as 'CE' | 'BCE')
      setBirthDateInput(displayWithEra)
      const sign = (eraHint === 'BCE') ? '-' : ''
      const stored = y ? `${dd}-${mm}-${sign}${y}` : displayWithEra
      setFormData(prev => ({ ...prev, birthDate: stored }))
      const err = validateDateParts(dd, mm, y, eraHint as 'CE' | 'BCE')
      setBirthDateError(err)
    }

    const toggleEra = () => {
      const next = birthEra === 'CE' ? 'BCE' : 'CE'
      setBirthEra(next)
      const m = birthDateInput.match(/^(\d{1,2})-(\d{1,2})-(?:-)?(\d{1,6})$/)
      if (m) {
        const dd = m[1], mm = m[2], y = m[3]
        const display = next === 'BCE' ? `${dd}-${mm}--${y}` : `${dd}-${mm}-${y}`
        setBirthDateInput(display)
        const sign = next === 'BCE' ? '-' : ''
        setFormData(prev => ({ ...prev, birthDate: `${dd}-${mm}-${sign}${y}` }))
        const err = validateDateParts(dd, mm, y, next)
        setBirthDateError(err)
      }
    }

    const handleBirthTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      const { hhmm, display, complete } = parseHumanTimeInput(value)
      
      setBirthTimeInput(value)
      setBirthTimeDisplay(display)
      
      if (complete) {
        setFormData(prev => ({ ...prev, birthTime: hhmm }))
      }
    }

    const handleBirthTimeBlur = () => {
      const { hhmm, display, normalizedInput, suffixIntent } = parseHumanTimeInput(birthTimeInput || birthTimeDisplay)
      if (hhmm) {
        setFormData(prev => ({ ...prev, birthTime: hhmm }))
        setBirthTimeDisplay(display)
        if (normalizedInput) {
          setBirthTimeInput(suffixIntent ? `${normalizedInput} ${suffixIntent}` : normalizedInput)
        }
      }
    }

    return { handleInputChange, handleBirthDateInputChange, toggleEra, handleBirthTimeChange, handleBirthTimeBlur }
  }

  const boyHandlers = createHandlers(setBoyFormData, setBoyBirthDateInput, setBoyBirthEra, setBoyBirthDateError, boyBirthEra, boyBirthDateInput, setBoyBirthTimeDisplay, setBoyBirthTimeInput, boyBirthTimeInput, boyBirthTimeDisplay)
  const girlHandlers = createHandlers(setGirlFormData, setGirlBirthDateInput, setGirlBirthEra, setGirlBirthDateError, girlBirthEra, girlBirthDateInput, setGirlBirthTimeDisplay, setGirlBirthTimeInput, girlBirthTimeInput, girlBirthTimeDisplay)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const processPerson = (data: PersonFormData, timeInput: string, timeDisplay: string) => {
        const errors: string[] = []
        let finalBirthTime = data.birthTime
        if (!finalBirthTime) {
            const parsed = parseHumanTimeInput(timeInput || timeDisplay)
            finalBirthTime = parsed.hhmm
        }
        if (!finalBirthTime) errors.push(t('chat.compatibility_form.errors.birth_time_required'))

        let y = 0, m = 1, d = 1
        const dmy = /^(\d{1,2})-(\d{1,2})-(-?\d{1,6})$/.exec(data.birthDate)
        if (dmy) {
            d = parseInt(dmy[1], 10)
            m = parseInt(dmy[2], 10)
            y = parseInt(dmy[3], 10)
        } else {
            errors.push(t('chat.astro_form.errors.birth_date_format'))
        }

        if (!errors.length) {
          if (y < -13200 || y > 17191) errors.push(t('chat.astro_form.errors.birth_year_out_of_range'))
          // Must be a date that EXISTS, not merely one whose parts are in range.
        // Manual-timezone mode can otherwise reach submit with 31 February and
        // get a server error instead of the inline date message.
        if (!isRealCalendarDate(y, m, d)) errors.push(t('chat.astro_form.errors.invalid_month_day'))
        }

        const timeMatch = /^([0-9]{2}):([0-9]{2})$/.exec(finalBirthTime || '')
        if (!timeMatch) errors.push(t('chat.astro_form.errors.birth_time_format'))
        if (timeMatch) {
          const hh = parseInt(timeMatch[1], 10)
          const mm = parseInt(timeMatch[2], 10)
          if (hh < 0 || hh > 23 || mm < 0 || mm > 59) errors.push(t('chat.astro_form.errors.birth_time_out_of_range'))
        }

        const lat = parseFloat(data.latitude)
        const lon = parseFloat(data.longitude)
        const tz = parseFloat(data.timezoneOffset)
        // Autofill/typing fills the city text without picking a suggestion, so
        // lat/lon never resolve. Say that plainly instead of "latitude range".
        const locationUnresolved = (Number.isNaN(lat) || Number.isNaN(lon)) && !!data.locationName.trim()
        if (locationUnresolved) {
            errors.push(t('chat.location_input.pick_from_list'))
        } else {
            if (Number.isNaN(lat) || lat < -90 || lat > 90) errors.push(t('chat.astro_form.errors.latitude_range'))
            if (Number.isNaN(lon) || lon < -180 || lon > 180) errors.push(t('chat.astro_form.errors.longitude_range'))
            if (Number.isNaN(tz) || tz < -14 || tz > 14) errors.push(t('chat.astro_form.errors.timezone_range'))
        }
        if (!data.name.trim()) errors.push(t('chat.astro_form.errors.name_required'))

        return {
            isValid: errors.length === 0,
            errors,
            payload: {
                dob_year: y, dob_month: m, dob_day: d,
                tob_hour: parseInt((finalBirthTime || '0:0').split(':')[0]),
                tob_minute: parseInt((finalBirthTime || '0:0').split(':')[1]),
                tob_second: 0,
                place_name: data.locationName || t('chat.location_input.unknown_location'),
                latitude: lat,
                longitude: lon,
                timezone: tz
            }
        }
      }

      const boyResult = processPerson(boyFormData, boyBirthTimeInput, boyBirthTimeDisplay)
      const girlResult = processPerson(girlFormData, girlBirthTimeInput, girlBirthTimeDisplay)

      if (!boyResult.isValid) throw new Error(`${t('chat.compatibility_form.boy_details')}: ${boyResult.errors.join(', ')}`)
      if (!girlResult.isValid) throw new Error(`${t('chat.compatibility_form.girl_details')}: ${girlResult.errors.join(', ')}`)

      const tzMessage = t('chat.birth_timezone.blocked_submit', {
        defaultValue: 'Please resolve the timezone for the birth place and time first.',
      })
      if (!boyTzState.usable) {
        throw new Error(`${t('chat.compatibility_form.boy_details')}: ${tzMessage}`)
      }
      if (!girlTzState.usable) {
        throw new Error(`${t('chat.compatibility_form.girl_details')}: ${tzMessage}`)
      }

      // Backend Health Check
      const baseUrl = import.meta.env.VITE_APP_SERVER || 'http://localhost:8000';

      if (!baseUrl) throw new Error(t('chat.compatibility_form.errors.backend_unreachable'))

      const user = auth.currentUser
      if (!user) throw new Error(t('chat.compatibility_form.errors.must_login'))

      const headers = await getSecureHeaders()

      const boyY = boyResult.payload.dob_year
      const boyM = String(boyResult.payload.dob_month).padStart(2, '0')
      const boyD = String(boyResult.payload.dob_day).padStart(2, '0')
      const boySign = boyY < 0 ? '-' : ''
      const boyISO = `${boySign}${Math.abs(boyY)}-${boyM}-${boyD}`
      const boyT = `${String(boyResult.payload.tob_hour).padStart(2,'0')}:${String(boyResult.payload.tob_minute).padStart(2,'0')}:00`

      const girlY = girlResult.payload.dob_year
      const girlM = String(girlResult.payload.dob_month).padStart(2, '0')
      const girlD = String(girlResult.payload.dob_day).padStart(2, '0')
      const girlSign = girlY < 0 ? '-' : ''
      const girlISO = `${girlSign}${Math.abs(girlY)}-${girlM}-${girlD}`
      const girlT = `${String(girlResult.payload.tob_hour).padStart(2,'0')}:${String(girlResult.payload.tob_minute).padStart(2,'0')}:00`

      const payload = {
        boy_uid: user.uid,
        boy_name: boyFormData.name,
        boy_dob: boyISO,
        boy_tob: boyT,
        boy_place: boyResult.payload.place_name,
        boy_lat: boyResult.payload.latitude,
        boy_lon: boyResult.payload.longitude,
        boy_tz: boyResult.payload.timezone,
        boy_timezone_source: boyFormData.timezoneSource || null,
        boy_timezone_fold: boyFormData.timezoneFold,
        boy_timezone_resolver_version: boyFormData.timezoneResolverVersion,
        boy_country: boyFormData.country || null,
        boy_country_name: boyFormData.countryName || null,

        girl_uid: user.uid,
        girl_name: girlFormData.name,
        girl_dob: girlISO,
        girl_tob: girlT,
        girl_place: girlResult.payload.place_name,
        girl_lat: girlResult.payload.latitude,
        girl_lon: girlResult.payload.longitude,
        girl_tz: girlResult.payload.timezone,
        girl_timezone_source: girlFormData.timezoneSource || null,
        girl_timezone_fold: girlFormData.timezoneFold,
        girl_timezone_resolver_version: girlFormData.timezoneResolverVersion,
        girl_country: girlFormData.country || null,
        girl_country_name: girlFormData.countryName || null,
        // Hints only — the backend re-resolves each partner independently.
        boy_timezone_name: boyFormData.timezoneName || null,
        girl_timezone_name: girlFormData.timezoneName || null
      }

      const response = await fetch(`${baseUrl}/calculate/compatibility`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        let detail: unknown = null
        try {
          detail = (await response.json())?.detail
        } catch {
          detail = null
        }
        const code =
          typeof detail === 'string'
            ? detail
            : detail && typeof detail === 'object' && !Array.isArray(detail)
              ? (detail as { code?: unknown }).code
              : null
        throw new Error(
          code === 'profile_limit_reached'
            ? t('chat.astro_form.toasts.profile_limit_description')
            : t('chat.compatibility_form.errors.failed_calculate'),
        )
      }

      const data = await response.json()
      onCompatibilityGenerated(data)
      toast({ title: t('chat.compatibility_form.toasts.success_title'), description: t('chat.compatibility_form.toasts.success_description') })

    } catch (error: unknown) {
        const description = error instanceof Error ? error.message : t('chat.compatibility_form.errors.failed_calculate')
        toast({ title: t('chat.compatibility_form.toasts.error_title'), description, variant: "destructive" })
    } finally {
        setLoading(false)
    }
  }

  const renderFormSection = (title: string, data: PersonFormData, handlers: ReturnType<typeof createHandlers>, dateInput: string, timeInput: string, timeDisplay: string, dateError: string, era: string, setFormData: React.Dispatch<React.SetStateAction<PersonFormData>>, onTzStateChange: (s: BirthTimezoneState) => void) => (
    <Card className="flex-1">
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {title}
            </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
            <div className="space-y-2">
                <Label>{t('chat.compatibility_form.name')}</Label>
                <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                  <User className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
                  <input
                    name="name"
                    value={data.name}
                    onChange={handlers.handleInputChange}
                    placeholder={t('chat.compatibility_form.name')}
                    className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    required
                  />
                </div>
            </div>
            <div className="space-y-2">
                <Label>{t('chat.astro_form.birth_date_label')}</Label>
                <div className={`flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${dateError ? 'border-red-500 focus-within:ring-red-500' : ''}`}>
                    <Calendar className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
                    <input 
                        value={dateInput} 
                        onChange={handlers.handleBirthDateInputChange} 
                        placeholder={t('chat.astro_form.birth_date_placeholder')} 
                        className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        aria-invalid={!!dateError}
                    />
                     <div className="mr-3 shrink-0">
                        <button type="button" onClick={handlers.toggleEra} className="text-xs px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">
                            {era}
                        </button>
                    </div>
                </div>
                <div className="text-xs text-gray-500 mt-1">{t('chat.astro_form.birth_date_support_range')}</div>
                {dateError && <div className="text-xs text-red-500 mt-1">{dateError}</div>}
            </div>
            <div className="space-y-2">
                <Label>{t('chat.astro_form.birth_time')}</Label>
                <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <Clock className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
                    <input 
                        value={timeInput} 
                        onChange={handlers.handleBirthTimeChange} 
                        onBlur={handlers.handleBirthTimeBlur}
                        placeholder={t('chat.compatibility_form.birth_time_placeholder')} 
                        className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    />
                </div>
                {timeDisplay && <div className="text-xs text-gray-500 mt-1">{t('chat.astro_form.converted_prefix')} {timeDisplay}</div>}
            </div>
            
            <div className="border-t pt-4">
                <LocationInput 
                    data={data} 
                    onChange={(d) => setFormData(prev => ({ ...prev, ...d }))} 
                    showInlineHelp={false}
                    timezoneSlot={
                      <BirthTimezoneField
                        birthDate={toIsoBirthDate(data.birthDate)}
                        birthTime={data.birthTime}
                        latitude={data.latitude}
                        longitude={data.longitude}
                        timezoneName={data.timezoneName}
                        value={data.timezoneOffset}
                        source={data.timezoneSource}
                        onChange={(next) =>
                          setFormData(prev => ({
                            ...prev,
                            timezoneOffset: next.timezoneOffset,
                            timezoneName: next.timezoneName ?? prev.timezoneName,
                            timezoneSource: next.timezoneSource,
                            timezoneFold: next.timezoneFold,
                            timezoneResolverVersion: next.timezoneResolverVersion,
                          }))
                        }
                        onStateChange={onTzStateChange}
                      />
                    }
                />
            </div>
        </CardContent>
    </Card>
  )

  return (
    <form onSubmit={handleSubmit} className="space-y-6 relative">
        <div className="absolute top-0 right-0 -mt-2 z-10">
            <button 
                type="button" 
                onClick={() => {
                    if(window.confirm(t('chat.compatibility_form.clear_details_confirm'))) {
                        // Built fresh per partner rather than sharing one object —
                        // two pieces of state must never alias the same reference.
                        const makeEmpty = (): PersonFormData => ({
                          name: '', birthDate: '', birthTime: '', latitude: '', longitude: '',
                          timezoneOffset: '', timezoneName: '', timezoneSource: '', timezoneFold: null,
                          timezoneResolverVersion: null, locationName: '', country: '', countryName: ''
                        });
                        const emptyTz: BirthTimezoneState = {
                          offsetHours: null, timezoneName: null, status: 'idle', usable: false,
                          source: null, fold: null, resolverVersion: null,
                        };
                        setBoyFormData(makeEmpty());
                        setGirlFormData(makeEmpty());
                        setBoyTzState(emptyTz);
                        setGirlTzState(emptyTz);
                        setBoyBirthTimeInput('');
                        setGirlBirthTimeInput('');
                        setBoyBirthDateInput('');
                        setGirlBirthDateInput('');
                        setBoyBirthTimeDisplay('');
                        setGirlBirthTimeDisplay('');
                        localStorage.removeItem('boyFormData');
                        localStorage.removeItem('girlFormData');
                    }
                }}
                className="text-xs text-gray-400 hover:text-red-500 underline transition-colors"
            >
                {t('chat.astro_form.reset_form')}
            </button>
        </div>
        <div className="flex flex-col md:flex-row gap-6">
            {renderFormSection(t('chat.compatibility_form.boy_details'), boyFormData, boyHandlers, boyBirthDateInput, boyBirthTimeInput, boyBirthTimeDisplay, boyBirthDateError, boyBirthEra, setBoyFormData, setBoyTzState)}
            {renderFormSection(t('chat.compatibility_form.girl_details'), girlFormData, girlHandlers, girlBirthDateInput, girlBirthTimeInput, girlBirthTimeDisplay, girlBirthDateError, girlBirthEra, setGirlFormData, setGirlTzState)}
        </div>
        <Button
          type="submit"
          className="w-full bg-pink-600 hover:bg-pink-700 py-6 text-lg"
          disabled={loading || boyTzState.status === 'resolving' || girlTzState.status === 'resolving'}
        >
            {loading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
            <Heart className="mr-2 h-5 w-5" />
            {loading ? t('chat.compatibility_form.generating_report') : t('chat.compatibility_form.check_compatibility')}
        </Button>
    </form>
  )
}

export default MarriageCompatibilityForm
